import $ from 'jquery';
import { modifyState } from '~src/state';
import { prepareImageForEditing } from '~core/image';
import { showSidebarTab } from '~src/sidebarLegacy';
import { isPlaceholderEditing } from '~core/magicFrame';
import { checkAiCreditsBeforeUse } from '~core/aiCredits';
import { DISSOLVENZA_BACKGROUNDS } from '~core/aiEdit/dissolvenzaBackgrounds';
import handleAiEdit from '~core/aiEdit/handleAiEdit';
import { colorPickerAPI } from '~core/colorPicker';

/**
 * Initialize dissolvenza sidebar with background options
 */
function initDissolvenzaSidebar() {
  // Populate gradient backgrounds
  const gradientGrid = $('#dissolvenza-gradient-grid');
  gradientGrid.empty();

  const gradientBackgrounds = DISSOLVENZA_BACKGROUNDS.filter(
    (bg) => bg.type === 'gradient'
  );
  gradientBackgrounds.forEach((bg) => {
    // For gradient backgrounds, use color preview instead of image
    const previewContent = bg.preview.startsWith('#')
      ? `<div class="dissolvenza-color-preview" style="background-color: ${bg.preview}; width: 100%; height: 100%;"></div>`
      : `<img src="${bg.preview}" alt="${bg.name}" class="dissolvenza-gradient-image" />`;

    gradientGrid.append(`
      <div class="dissolvenza-bg-item" data-bg-id="${bg.id}" data-bg-key="${bg.key}">
        <div class="dissolvenza-bg-preview-wrapper">
          <div class="dissolvenza-bg-preview dissolvenza-oval">
            ${previewContent}
          </div>
        </div>
        <div class="dissolvenza-bg-name">${bg.name}</div>
      </div>
    `);
  });

  // Populate nature backgrounds
  const natureGrid = $('#dissolvenza-nature-grid');
  natureGrid.empty();

  const natureBackgrounds = DISSOLVENZA_BACKGROUNDS.filter(
    (bg) => bg.type === 'nature'
  );
  natureBackgrounds.forEach((bg) => {
    natureGrid.append(`
      <div class="dissolvenza-bg-item" data-bg-id="${bg.id}" data-bg-key="${bg.key}">
        <div class="dissolvenza-bg-preview-wrapper">
          <div class="dissolvenza-bg-preview dissolvenza-oval">
            <img src="${bg.preview}" alt="${bg.name}" class="dissolvenza-nature-image" />
          </div>
        </div>
        <div class="dissolvenza-bg-name">${bg.name}</div>
      </div>
    `);
  });
}

/**
 * Stop dissolvenza mode and cleanup
 */
function stopDissolvenza() {
  modifyState('isDissolvenzaActive', false);
  modifyState('dissolvenzaOriginalImage', null);
  modifyState('dissolvenzaSelection', null);
  modifyState('dissolvenzaSelectedBg', null);

  // Reset UI
  $('#dissolvenza-mode-selection').show();
  $('#dissolvenza-bg-container').hide();
  $('.dissolvenza-bg-item').removeClass('selected');

  // Return to AI sidebar
  showSidebarTab('edit-images-ai');
}

/**
 * Handle background selection and apply immediately
 */
function handleBackgroundSelection() {
  $(document).off('click', '.dissolvenza-bg-item');
  $(document).on('click', '.dissolvenza-bg-item', async function () {
    // Store selected background
    const bgKey = $(this).data('bg-key');

    // Apply dissolvenza with selected background immediately
    const prompt = `DISSOLVENZA_${bgKey}`;
    await handleAiEdit(prompt);
    stopDissolvenza();
  });
}

/**
 * Handle custom color selection using color picker
 */
async function handleCustomColorSelection() {
  try {
    // Initialize color picker API if not already done
    await colorPickerAPI.init();

    // Show modal color picker with dissolvenza-friendly default
    const selectedColor = await new Promise((resolve, reject) => {
      colorPickerAPI.quickPick(
        '#E6F3FF',
        (color) => {
          resolve(color);
        },
        {
          format: 'hex', // Force hex format for dissolvenza
          showAlpha: false, // No alpha for dissolvenza
          showAdvanced: false, // Keep it simple
          onCancel: () => {
            reject(new Error('Color selection cancelled'));
          },
        }
      );
    });

    // Apply dissolvenza with custom color
    // Use the same pattern as gradient backgrounds with GRADIENT_ prefix and uppercase hex
    const customColorKey = selectedColor.replace('#', '').toUpperCase();
    const prompt = `DISSOLVENZA_GRADIENT_#${customColorKey}`;

    await handleAiEdit(prompt);
    stopDissolvenza();
  } catch (error) {
    if (error.message !== 'Color selection cancelled') {
      console.error('Error in custom color selection:', error);
      alert('Errore nella selezione del colore personalizzato. Riprova.');
    }
  }
}

export default async function startDissolvenza() {
  // Check credits before entering the feature
  const hasCredits = await checkAiCreditsBeforeUse({
    featureName: 'Dissolvenza',
    requiredCredits: 1,
  });

  if (!hasCredits) {
    return;
  }

  // Get current selection
  let selection = window.canvas.getActiveObject();
  if (
    !selection ||
    (selection.type !== 'image' && selection.type !== 'MagicFrame')
  ) {
    alert("Seleziona un'immagine per applicare l'effetto dissolvenza");
    return;
  }

  if (selection.type === 'MagicFrame') {
    selection = selection.editingImg;
  }

  if (isPlaceholderEditing(selection)) {
    return;
  }

  const imageUrl = await prepareImageForEditing(selection, false);

  if (!imageUrl) {
    console.error('No image URL found in current selection', selection);
    return;
  }

  // Store original state
  modifyState('dissolvenzaOriginalImage', imageUrl);
  modifyState('dissolvenzaSelection', selection);
  modifyState('isDissolvenzaActive', true);

  // Initialize sidebar content
  initDissolvenzaSidebar();
  handleBackgroundSelection();

  // Show dissolvenza sidebar tab
  showSidebarTab('dissolvenza');

  // Show the dissolvenza container
  $('#sidebar-box-dissolvenza-container').show();

  // Reset to initial state
  $('#dissolvenza-mode-selection').show();
  $('#dissolvenza-bg-container').hide();

  // Scroll sidebar to top
  $('#sidebar-content').scrollTop(0);

  // Handle mode selection buttons
  $('#dissolvenza-original-bg')
    .off('click')
    .on('click', async function () {
      // Apply dissolvenza with original background
      await handleAiEdit('DISSOLVENZA');
      stopDissolvenza();
    });

  $('#dissolvenza-replace-bg')
    .off('click')
    .on('click', function () {
      // Show background selection
      $('#dissolvenza-mode-selection').hide();
      $('#dissolvenza-bg-container').show();
    });

  // Handle abort button
  $('#editor-menu-abort-dissolvenza')
    .off('click')
    .on('click', function () {
      stopDissolvenza();
    });

  // Handle custom color selection button
  $('#btn-custom-color-dissolvenza')
    .off('click')
    .on('click', async function () {
      await handleCustomColorSelection();
    });
}

// Export additional functions for external use
export { stopDissolvenza };
